'use client';

import React from 'react';
import type { TocItem } from 'app/blog/[domain]/(standalone)/_server/types';
import clsx from 'clsx';
import { useOnChange } from 'hooks/use-on-change';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FocusableLink } from 'ui';
import { useActiveAnchors } from './use-anchor-observer';

export interface TOCItemProps {
  onActiveChange?: (v: boolean) => void;
  item: TocItem;
  upper?: number;
  lower?: number;

  containerRef: React.RefObject<HTMLElement | null>;
}

export function TOCItem({
  onActiveChange,
  item,
  upper = item.level,
  lower = item.level,
  containerRef,
}: TOCItemProps) {
  const activeAnchors = useActiveAnchors();
  const anchorRef = React.useRef<HTMLAnchorElement>(null);

  const isActive = activeAnchors.includes(item.id);

  useOnChange(isActive, (_isActive) => {
    const element = anchorRef.current;
    if (!element) {
      return;
    }

    if (_isActive && containerRef.current) {
      scrollIntoView(element, {
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
        scrollMode: 'always',
        boundary: containerRef.current,
      });
    }

    onActiveChange?.(_isActive);
  });

  const offset = getLineOffset(item.level);
  const upperOffset = getLineOffset(upper);
  const lowerOffset = getLineOffset(lower);

  return (
    <FocusableLink
      ref={anchorRef}
      data-active={isActive}
      href={`#${item.id}`}
      style={{
        paddingInlineStart: getItemOffset(item.level),
      }}
      className="pe-2 text-tertiary transition data-[active=true]:text-brand-600 hover:text-tertiary-hover hover:data-[active=true]:text-brand-700 dark:data-[active=true]:text-gray-300 dark:hover:data-[active=true]:text-gray-200"
    >
      {offset !== upperOffset ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          className="absolute -top-1.5 start-0 size-4 rtl:-scale-x-100"
        >
          <line x1={upperOffset} y1="0" x2={offset} y2="12" strokeWidth="1" />
        </svg>
      ) : null}
      <span
        className={clsx(
          'absolute inset-y-0 block w-px',
          offset !== upperOffset && 'top-1.5',
          offset !== lowerOffset && 'bottom-1.5',
        )}
        style={{
          insetInlineStart: offset,
        }}
      />
      {item.text}
    </FocusableLink>
  );
}

export function getItemOffset(depth: number): number {
  if (depth <= 2) {
    return 14;
  }
  if (depth === 3) {
    return 26;
  }
  return 36;
}

export function getLineOffset(depth: number): number {
  return depth >= 3 ? 10 : 0;
}
